import {
  AdCreative,
  AdCreativeStatus,
  AdCreativeType,
  AssetApprovalStatus,
  CampaignConfig,
  CampaignGoalType,
  CreativeGroup,
  needsConversionSelection,
  OptimizationDefinition,
  OptimizationGoal,
  OutcomeCampaignObjective,
  PixelEventType,
  placementsServeStoryAndReels,
  PublishCampaignDTO,
  resolveOptimizationDefinition,
  SavedAudience,
  Seconds,
} from '@marketing-milk/interfaces'
import { exists, rejectUndefined } from 'app/util'
import { addMonths, fromUnixTime, getUnixTime, isBefore } from 'date-fns'
import {
  add,
  all,
  any,
  concat,
  filter,
  flattenDeep,
  isEmpty,
  isEqual,
  map,
  mean,
  pipe,
  prop,
  propEq,
  reduce,
  update,
} from 'lodash/fp'
import { SpendLimits } from 'pages/business/campaign-builder/steps/audience'
import { toast } from 'react-toastify'
import { AdCreativeGroup } from '../forms/CampaignForm'
import { cannotDialForCalls, isValidCreative } from './campaign.helpers'
import { zonedTimeToUtc } from 'date-fns-tz'

export const getAudiencesById = (ids: number[] = [], audiences: SavedAudience[] = []) =>
  audiences.filter(({ id }) => ids.includes(id))

/**
 * An audience the connected ad account no longer holds cannot be published against. The flag is
 * absent whenever availability wasn't evaluated, which reads as available.
 */
export const isAudienceAvailable = (audience: SavedAudience) =>
  audience.availableInAdAccount !== false

/**
 * Why an unavailable audience can't be used. Names the shared audiences when the source ad account
 * they came from is disabled, since that is something the AE has to fix by replacing them.
 */
export const audienceUnavailableMessage = (audience: SavedAudience) =>
  audience.disabledSourceAudiences?.length
    ? `Not available — uses audiences shared from a disabled ad account: ${audience.disabledSourceAudiences.join(
        ', '
      )}`
    : 'Not available in the connected ad account'

/** An unavailable audience's name, with the disabled-source audiences behind it when that is why. */
export const describeUnavailableAudience = (audience: SavedAudience) =>
  audience.disabledSourceAudiences?.length
    ? `${
        audience.name
      } (uses audiences shared from a disabled ad account: ${audience.disabledSourceAudiences.join(
        ', '
      )})`
    : audience.name

export const getDefaultAudiences = (audiences: SavedAudience[]) =>
  audiences.filter(audience => audience.isDefault && isAudienceAvailable(audience))

export const getDefaultAudienceIDs = (audiences: SavedAudience[]) =>
  getDefaultAudiences(audiences).map(audience => audience.id)

export const sanitizeSpendLimits = (
  spendLimits: PublishCampaignDTO['spendLimitsByAudienceId']
): SpendLimits => (spendLimits ? spendLimits : {})

export const getAverageAudienceSize = ({
  approximateCountLowerBound,
  approximateCountUpperBound,
}: SavedAudience) => mean([approximateCountLowerBound, approximateCountUpperBound])

export const getAudienceBoundsAverageTotal = pipe(map(getAverageAudienceSize), reduce(add, 0))

export const calculateFinalBudget = (base: number, upsellPercent?: number) => {
  if (!upsellPercent) return base
  return base + base * (upsellPercent / 100)
}

export const nameIsValid = (name?: string) => (name ? exists(name) && name.length < 256 : false)
export const optimizationIsValid = (campaign?: CampaignConfig) =>
  !!campaign && !!resolveOptimizationDefinition(campaign)
// Reads the same catalog the server does, so the builder and publish agree by construction
// rather than by two parallel rule sets.
export const addtlGoalCampaignConfigIsValid = (campaignConf: CampaignConfig) =>
  !needsConversionSelection(campaignConf)
export const audiencesAreValid = (audienceIds?: number[]) =>
  audienceIds ? exists(audienceIds) : false
export const budgetIsValid = (budget?: number) => !!(budget ? budget > 49 && budget < 10000 : false)
export const campaignHasStarted = (startTime?: number): boolean =>
  !!startTime && startTime <= Math.floor(new Date().getTime() / 1000)
export const campaignHasEnded = (endTime: number): boolean =>
  endTime <= Math.floor(new Date().getTime() / 1000)

export const nameErrorText = (name?: string) => {
  if (!name) return 'name is required'
  if (name.length > 255) return 'name must be under 256 characters'
  return ''
}

export const budgetErrorText = (budget?: number) => {
  if (!budget) return 'no budget selected'
  if (budget < 49) return 'budget must exceed $50'
  if (budget > 10000) return 'budget must be under $10,000'
  return ''
}

/** A half-typed date in a picker comes out as NaN, which a saved draft holds as null (DRI-518). */
export const isValidTime = (time?: Seconds | null): time is Seconds =>
  Number.isFinite(time) && time !== 0

export const dateErrorText = ([start, end]: [
  Seconds | null | undefined,
  Seconds | null | undefined
]) => {
  if (!isValidTime(start)) return 'start date is required'
  if (!isValidTime(end)) return 'end date is required'
  if (isBefore(end, start)) return 'end date cannot be before start date'
  return ''
}

export const campaignSettingsValidators = (dto?: PublishCampaignDTO) => {
  const nameValid = dto?.campaign?.name ? nameIsValid(dto?.campaign?.name) : false
  const budgetValid = dto?.campaign?.budget ? budgetIsValid(dto?.campaign?.budget) : false
  const goalValid = dto?.campaign
    ? optimizationIsValid(dto.campaign) && addtlGoalCampaignConfigIsValid(dto.campaign)
    : false
  return [nameValid, budgetValid, goalValid]
}

export const getValidCreatives = (creativeGroups: CreativeGroup[], creatives?: AdCreative[]) =>
  pipe(
    creatives => getAdCreativeGroups(creativeGroups, creatives),
    flattenDeep,
    filter(isValidCreative),
    rejectUndefined
  )(creatives || [])

/**
 * The ads on this campaign the client has rejected. A rejection is the client's, so it
 * withholds the ad wherever the campaign is looked at — the draft table names them, and
 * the launcher refuses the campaign until they are removed or the rejection is cleared.
 */
export const getRejectedCreatives = (creativeGroups: CreativeGroup[], creatives?: AdCreative[]) =>
  pipe(
    (creatives: AdCreative[]) => getAdCreativeGroups(creativeGroups, creatives),
    flattenDeep,
    rejectUndefined,
    filter((creative: AdCreative) => creative.reviewStatus === AssetApprovalStatus.Rejected)
  )(creatives || [])

export const anyCreativesValid = (creatives: AdCreative[]) =>
  any(isEqual(true), creatives.map(isValidCreative))

export const allCreativesValid = (creatives: AdCreative[]) =>
  all(isEqual(true), creatives.map(isValidCreative))

export const campaignDtoHasValidAudiences = (dto: PublishCampaignDTO) => {
  if (!dto) return false
  if (!dto.audienceIDs) return false
  if (dto.audienceIDs.length === 0) return false
  return true
}

// Per-placement ads carry a Story & Reels cut; a campaign that never serves there is blocked.
export const placementsMissStory = (dto: PublishCampaignDTO, creatives: AdCreative[]) =>
  rejectUndefined(getAdCreativeGroups(dto.creativeGroups, creatives).flat()).some(
    creative => creative.format === AdCreativeType.per_placement
  ) && !placementsServeStoryAndReels(dto.publisherPlatforms, dto.placementPositions)

/**
 * Why a campaign can't publish, one entry per reason. The draft table names each of these and the
 * launcher refuses anything with one, so the two read the same list instead of keeping their own;
 * a new rule belongs here and nowhere else.
 */
export type PublishBlocker =
  | { kind: 'missingOptimization' }
  | { kind: 'missingConversion' }
  | { kind: 'invalidName' }
  | { kind: 'invalidBudget' }
  | { kind: 'invalidDates'; reason: string }
  | { kind: 'missingAudiences' }
  | { kind: 'unavailableAudiences'; audiences: SavedAudience[] }
  | { kind: 'incompleteCreative'; creative: AdCreative }
  | { kind: 'rejectedCreatives'; creatives: AdCreative[] }
  | { kind: 'dynamicSharesGroup'; creatives: AdCreative[] }
  | { kind: 'noCreatives' }
  | { kind: 'emptyAdGroup'; index: number }
  | { kind: 'missingLeadForms'; creatives: AdCreative[] }
  | { kind: 'missingCallNow'; creatives: AdCreative[] }
  | { kind: 'placementsMissStory' }

/** An ad group as the Choose Ads tabs label it, so an error points at a tab the AE can find. */
export const adGroupLabel = (index: number) =>
  index === 0 ? 'Main Ad Group' : `Variation #${index}`

export const getCampaignPublishBlockers = (
  dto: PublishCampaignDTO,
  creatives: AdCreative[] = [],
  audiences?: SavedAudience[]
): PublishBlocker[] => {
  const blockers: PublishBlocker[] = []
  const optimization = dto.campaign ? resolveOptimizationDefinition(dto.campaign) : undefined

  if (!optimization) blockers.push({ kind: 'missingOptimization' })
  else if (!addtlGoalCampaignConfigIsValid(dto.campaign))
    blockers.push({ kind: 'missingConversion' })
  if (!nameIsValid(dto.campaign?.name)) blockers.push({ kind: 'invalidName' })
  if (!budgetIsValid(dto.campaign?.budget)) blockers.push({ kind: 'invalidBudget' })
  const dateError = dateErrorText([dto.campaign?.startTime, dto.campaign?.endTime])
  if (dateError) blockers.push({ kind: 'invalidDates', reason: dateError })

  const unavailableAudiences = getAudiencesById(dto.audienceIDs, audiences).filter(
    audience => !isAudienceAvailable(audience)
  )
  if (!campaignDtoHasValidAudiences(dto)) blockers.push({ kind: 'missingAudiences' })
  else if (unavailableAudiences.length)
    blockers.push({ kind: 'unavailableAudiences', audiences: unavailableAudiences })

  // An id whose ad isn't in the sprint resolves to nothing and counts as absent, which is how a
  // group can be empty on publish though it still holds ids.
  const groups = getAdCreativeGroups(dto.creativeGroups ?? [], creatives).map(group =>
    rejectUndefined(group)
  )
  const campaignCreatives = groups.flat()
  campaignCreatives
    .filter(creative => !isValidCreative(creative))
    .forEach(creative => blockers.push({ kind: 'incompleteCreative', creative }))

  const rejectedCreatives = campaignCreatives.filter(
    creative => creative.reviewStatus === AssetApprovalStatus.Rejected
  )
  if (rejectedCreatives.length)
    blockers.push({ kind: 'rejectedCreatives', creatives: rejectedCreatives })

  // Meta builds a dynamic ad's combinations itself, so it is the whole ad group or it cannot publish.
  const dynamicSharingAGroup = groups
    .filter(group => group.length > 1)
    .flatMap(group => group.filter(creative => creative.format === AdCreativeType.dynamic))
  if (dynamicSharingAGroup.length)
    blockers.push({ kind: 'dynamicSharesGroup', creatives: dynamicSharingAGroup })

  if (!campaignCreatives.length) blockers.push({ kind: 'noCreatives' })
  else
    groups.forEach((group, index) => {
      if (!group.length) blockers.push({ kind: 'emptyAdGroup', index })
    })

  // Asked only of the complete ads; an incomplete one is already named for what it's missing.
  const validCreatives = campaignCreatives.filter(isValidCreative)
  const missingLeadForms = optimization?.requiresLeadCard
    ? validCreatives.filter(creative => !creative.leadCardUsed)
    : []
  if (missingLeadForms.length)
    blockers.push({ kind: 'missingLeadForms', creatives: missingLeadForms })

  const missingCallNow = validCreatives.filter(creative =>
    cannotDialForCalls(creative, optimization)
  )
  if (missingCallNow.length) blockers.push({ kind: 'missingCallNow', creatives: missingCallNow })

  if (placementsMissStory(dto, creatives)) blockers.push({ kind: 'placementsMissStory' })

  return blockers
}

export const isValidCampaignDto = (
  dto: PublishCampaignDTO,
  creatives: AdCreative[],
  audiences?: SavedAudience[]
) => getCampaignPublishBlockers(dto, creatives, audiences).length === 0

const AD_BLOCKER_KINDS: PublishBlocker['kind'][] = [
  'incompleteCreative',
  'dynamicSharesGroup',
  'noCreatives',
  'emptyAdGroup',
  'placementsMissStory',
]

/**
 * The publish rules about how a campaign's ads are grouped and built. The campaign form refuses
 * these on save too, reading them from here so its check can't drift from the launcher's.
 */
export const getCampaignAdBlockers = (dto: PublishCampaignDTO, creatives: AdCreative[]) =>
  getCampaignPublishBlockers(dto, creatives).filter(blocker =>
    AD_BLOCKER_KINDS.includes(blocker.kind)
  )

export type CampaignFormErrors = {
  campaign?: string[]
  adsets?: string[]
  ads?: string[]
}
export const singleCampaignDtoIsValid = (
  { campaign, ...dto }: PublishCampaignDTO,
  setErrors?: React.Dispatch<React.SetStateAction<CampaignFormErrors>>
) => {
  if (!nameIsValid(campaign.name)) {
    const errorText = nameErrorText(campaign.name)
    toast.error(errorText)
    setErrors && setErrors(update('campaign', concat(errorText)))
    return false
  }

  if (!optimizationIsValid(campaign)) {
    toast.error(`Please choose what ${campaign.name} optimizes for`)
    if (setErrors) {
      setErrors(update('campaign', concat('goal is empty')))
      setErrors({ campaign: ['goal is empty'] })
    }
    return false
  }

  if (!budgetIsValid(campaign.budget)) {
    const budgetText = budgetErrorText(campaign.budget)
    toast.error(`[${campaign.name} Error]: ${budgetText}`)
    setErrors && setErrors(update('campaign', concat(`[${campaign.name}]: ${budgetText}`)))
  }

  const dateError = dateErrorText([campaign.startTime, campaign.endTime])

  if (exists(dateError)) {
    toast.error(dateError)
    setErrors && setErrors({ campaign: [`[${campaign.name}]: ${dateError}`] })
    return false
  }

  if (isEmpty(dto.audienceIDs)) {
    toast.error(`Please Select an Audience for ${campaign.name}`)
    setErrors && setErrors({ adsets: [`No audience selected for ${campaign.name}`] })
    return false
  }

  if (any(isEmpty, dto.creativeGroups) || isEmpty(dto.creativeGroups)) {
    toast.error(`Please Select an Creatives for ${campaign.name}`)
    setErrors && setErrors({ ads: ['All Variations must have at least one creative selected'] })
    return false
  }

  return true
}

export const findCreativeById = (id?: number, creatives?: AdCreative[]) =>
  creatives?.find(propEq('id', id))

export const getOptimization = (campaign: CampaignConfig) => resolveOptimizationDefinition(campaign)

/**
 * Applies a chosen optimization to the DTO. The reporting bucket follows from the catalog, and
 * conversion state resets because the next optimization may offer a different set of events.
 */
export const applyOptimization =
  (optimization: OptimizationDefinition) =>
  (dto: PublishCampaignDTO): PublishCampaignDTO => ({
    ...dto,
    campaign: {
      ...dto.campaign,
      objective: optimization.objective,
      optimizationGoal: optimization.optimizationGoal,
      optimizationKey: optimization.key,
      goal: optimization.reportingGoal,
      pixelType: undefined,
      customConversionID: undefined,
    },
  })

/** A pixel event and a custom conversion are alternatives, so setting one clears the other. */
export const applyConversionSelection =
  (pixelEventOrCCID: PixelEventType | number) =>
  (dto: PublishCampaignDTO): PublishCampaignDTO => ({
    ...dto,
    campaign: {
      ...dto.campaign,
      ...(typeof pixelEventOrCCID === 'number'
        ? { customConversionID: pixelEventOrCCID, pixelType: undefined }
        : { pixelType: pixelEventOrCCID, customConversionID: undefined }),
    },
  })

export const getSelectableCreatives = (creatives: AdCreative[]) =>
  creatives
    .filter(creative => creative.status !== AdCreativeStatus.DELETED) // not deleted
    .filter(creative => creative.status !== AdCreativeStatus.WITH_ISSUES) // has no issues
    .filter(isValidCreative)

export const getCostPerText = (goal?: `${CampaignGoalType}`) => {
  if (goal === 'lead_generation') return 'cost per lead genenerated'
  if (goal === 'ecommerce_sales') return 'cost per website purchase'
  if (goal === 'website_sessions') return 'cost per visit'
  if (goal === 'in_store_sales') return 'cost per sale'
  return ''
}

export const getGoalColorClasses = (goal?: `${CampaignGoalType}`) => {
  if (goal === 'lead_generation') return 'text-blue-800 bg-blue-200'
  if (goal === 'ecommerce_sales') return 'text-green-800 bg-green-200'
  if (goal === 'website_sessions') return 'text-yellow-800 bg-yellow-200'
  if (goal === 'in_store_sales') return 'text-orange-800 bg-orange-200'
  return 'text-gray-800 bg-gray-200'
}

export const getIntervalFromDto = (dto: PublishCampaignDTO) => ({
  start: fromUnixTime(dto.campaign.startTime),
  end: fromUnixTime(dto.campaign.endTime),
})

export const getDefaultDTO = (
  dto: PublishCampaignDTO | undefined,
  timezone = 'America/Chicago'
): NonNullable<PublishCampaignDTO> => {
  if (dto) return dto
  return {
    campaign: {
      budget: 250,
      startTime: getUnixTime(zonedTimeToUtc(new Date().setHours(12, 0, 0, 0), timezone)),
      endTime: getUnixTime(
        addMonths(zonedTimeToUtc(new Date().setHours(12, 0, 0, 0), timezone), 3)
      ),
      frequency: 7, // Frequency should always be 7
      goal: CampaignGoalType.LeadGeneration,
      objective: OutcomeCampaignObjective.OUTCOME_LEADS,
      optimizationGoal: OptimizationGoal.LEAD_GENERATION,
      name: 'New Campaign',
    },
    audienceIDs: [],
    creativeGroups: [[]],
  }
}

export const getAdCreativeGroups = (
  creativeGroups: PublishCampaignDTO['creativeGroups'],
  creatives: AdCreative[]
) =>
  creativeGroups.map(group =>
    group.map(creativeId => creatives?.find(({ id }) => creativeId === id))
  )

export const getCreativeGroups = (adGroups: AdCreativeGroup[]) =>
  adGroups.map(group => group?.map(prop('id')))

export const combineTime = (newDate: Date, newTime: Date, timezone: string): Date => {
  const zonedTime = new Date(
    `${newDate.toDateString()} ${newTime.toTimeString().substring(0, 8)}` // trim off TZ meta data
  )
  const utcDate = zonedTimeToUtc(zonedTime, timezone)
  return utcDate
}
